'use client';
import React from 'react';

import { cn, stripNonHtmlIds } from '@/lib/utils';

import { Text } from '@/components/atoms//Text';
import Card from '@/components/atoms/Card';
import { OfferCountdownStatus } from '@/components/atoms/countdown';
import LinkButtonStrapi, {
  LinkButtonStrapiProps,
} from '@/components/atoms/LinkButton/LinkStrapi';
import { getBannerColorScheme, BannerColorScheme } from './helpers';
import { usePathname } from 'next/navigation';
import { useCurrentExperiment } from '@/lib/experimentation/useCurrentExperiment';
import { track } from '@/lib/tracker';

export type BannerProps = {
  title: string;
  description: React.ReactNode;
  direction: 'column' | 'row' | null;
  image: React.ReactNode;
  buttonIcon?: React.ReactNode;
  countdownExpiry?: string | null;
  button?: LinkButtonStrapiProps;
  colorScheme?: BannerColorScheme;
  classNames?: {
    root?: string;
  };
  shouldTrackEvent?: boolean;
};

export default function Banner({
  button,
  buttonIcon,
  image,
  title,
  description,
  direction,
  classNames,
  countdownExpiry,
  colorScheme = 'transparent',
  // TODO: remove this after pricing cards AB test
  shouldTrackEvent = false,
}: BannerProps) {
  const pathname = usePathname();
  const experiment = useCurrentExperiment();
  const hasCountdown = !!countdownExpiry;
  const bannerBg =
    colorScheme === 'transparent' ? 'bg-transparent' : 'bg-base-dark-white';
  const ctaBg =
    colorScheme === 'transparent'
      ? ''
      : getBannerColorScheme(colorScheme).ctaBg;

  const trackCtaClick = () => {
    try {
      const eventPayload = {
        pathname,
        is_in_experiment_page: !!experiment,
        ...(experiment && {
          exp_experiment_id: experiment.flagId,
          variant_id: experiment.variant,
        }),
      };
      track('contact_sales_cta_click', eventPayload);
    } catch {}
  };

  const buttonWithoutCmsId = button
    ? stripNonHtmlIds(
        button as LinkButtonStrapiProps & { id?: number | string }
      )
    : undefined;

  return (
    <div className={cn('w-full rounded', bannerBg)}>
      <Card
        color={getBannerColorScheme(colorScheme).borderColor}
        classNames={{
          root: cn(
            'flex flex-col gap-10 lg:gap-6 dark:bg-transparent bg-transparent p-6',
            classNames?.root,
            getBannerColorScheme(colorScheme).bannerBg
          ),
        }}
      >
        <div
          className={cn(
            'flex gap-4',
            direction === 'row'
              ? 'flex-col lg:flex-row lg:items-center'
              : 'flex-col'
          )}
        >
          <div
            className={cn(
              'flex flex-row items-center gap-4 text-left md:flex-grow md:gap-x-6',
              hasCountdown && 'gap-5 lg:gap-x-6 lg:gap-y-6',
              direction === 'row' && 'lg:flex-1'
            )}
          >
            {!!image && image}
            <div className='space-y-3 pt-5 md:pt-0'>
              <Text
                variant='subtitle-l-d'
                classNames={{
                  wrapper: cn(
                    'font-serif',
                    getBannerColorScheme(colorScheme).text
                  ),
                }}
              >
                {title}
              </Text>
              {!!description && (
                <Text
                  as='div'
                  classNames={{
                    wrapper: getBannerColorScheme(colorScheme).textSecondary,
                  }}
                >
                  {description}
                </Text>
              )}
            </div>
          </div>
          {buttonWithoutCmsId && (
            <div
              className={cn(
                'flex justify-start',
                direction === 'row' && 'lg:shrink-0'
              )}
              onClick={shouldTrackEvent ? trackCtaClick : undefined}
            >
              <LinkButtonStrapi
                block={buttonWithoutCmsId as LinkButtonStrapiProps}
                inner={{
                  classNames: {
                    root: cn(
                      'w-auto',
                      ctaBg,
                      getBannerColorScheme(colorScheme).ctaHoverBg,
                      getBannerColorScheme(colorScheme).ctaText
                    ),
                  },
                  leftSection: buttonIcon,
                }}
              />
            </div>
          )}
        </div>

        {hasCountdown && (
          <div className='mt-3 flex justify-center rounded-xl bg-gray-800 p-4'>
            <OfferCountdownStatus expiry={countdownExpiry} />
          </div>
        )}
      </Card>
    </div>
  );
}
